import React from 'react';
import { useAuth } from '../contexts/AuthContext';
import AppHeader from '../components/AppHeader';
import SecurityTab from '../components/admin/SecurityTab';
import { motion } from 'framer-motion';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { User as UserIcon, Mail, IdCard, Briefcase } from 'lucide-react';

/**
 * ProfilePage — every authenticated user (Primary Care, Consultant, NHS Manager,
 * Institution Admin, Super Admin) can reach this from the header user-menu.
 * Contains:
 *   - Read-only identity card (name, email, role, GMC / specialty)
 *   - MFA setup / disable flow (SecurityTab)
 */
export default function ProfilePage() {
    const { user, authToken } = useAuth();

    const roleLabel = (r) => (r ? r.replace(/_/g, ' ') : '—');

    return (
        <div className="min-h-screen bg-slate-50 dark:bg-slate-950">
            <AppHeader title="Profile & Security" />

            <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8">
                <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} className="space-y-6">
                    {/* Identity card */}
                    <Card data-testid="profile-identity-card">
                        <CardHeader>
                            <CardTitle className="flex items-center gap-2">
                                <UserIcon className="w-5 h-5 text-teal-600" />
                                Your profile
                            </CardTitle>
                            <CardDescription>
                                Read-only identity details on file. Contact your NHS Manager to change any of these.
                            </CardDescription>
                        </CardHeader>
                        <CardContent>
                            <dl className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm">
                                <div className="flex items-start gap-3">
                                    <UserIcon className="w-4 h-4 text-slate-400 mt-0.5 flex-shrink-0" />
                                    <div className="min-w-0 flex-1">
                                        <dt className="text-slate-500 text-xs uppercase tracking-wide">Full name</dt>
                                        <dd className="font-medium text-slate-900 dark:text-slate-100 truncate">
                                            {user?.full_name || '—'}
                                        </dd>
                                    </div>
                                </div>
                                <div className="flex items-start gap-3">
                                    <Mail className="w-4 h-4 text-slate-400 mt-0.5 flex-shrink-0" />
                                    <div className="min-w-0 flex-1">
                                        <dt className="text-slate-500 text-xs uppercase tracking-wide">Email</dt>
                                        <dd className="font-medium text-slate-900 dark:text-slate-100 break-all">
                                            {user?.email || '—'}
                                        </dd>
                                    </div>
                                </div>
                                <div className="flex items-start gap-3">
                                    <Briefcase className="w-4 h-4 text-slate-400 mt-0.5 flex-shrink-0" />
                                    <div className="min-w-0 flex-1">
                                        <dt className="text-slate-500 text-xs uppercase tracking-wide">Role</dt>
                                        <dd>
                                            <Badge variant="outline" className="capitalize">
                                                {roleLabel(user?.role)}
                                            </Badge>
                                        </dd>
                                    </div>
                                </div>
                                {user?.gmc_number && (
                                    <div className="flex items-start gap-3">
                                        <IdCard className="w-4 h-4 text-slate-400 mt-0.5 flex-shrink-0" />
                                        <div className="min-w-0 flex-1">
                                            <dt className="text-slate-500 text-xs uppercase tracking-wide">GMC Number</dt>
                                            <dd className="font-medium text-slate-900 dark:text-slate-100 font-mono text-xs">
                                                {user.gmc_number}
                                            </dd>
                                        </div>
                                    </div>
                                )}
                                {user?.specialty && (
                                    <div className="flex items-start gap-3">
                                        <IdCard className="w-4 h-4 text-slate-400 mt-0.5 flex-shrink-0" />
                                        <div className="min-w-0 flex-1">
                                            <dt className="text-slate-500 text-xs uppercase tracking-wide">Specialty</dt>
                                            <dd className="font-medium text-slate-900 dark:text-slate-100">
                                                {user.specialty}
                                            </dd>
                                        </div>
                                    </div>
                                )}
                            </dl>
                        </CardContent>
                    </Card>

                    {/* MFA / Security section — reuses the same component that lives on the Admin tab */}
                    <SecurityTab authToken={authToken} user={user} />
                </motion.div>
            </main>
        </div>
    );
}
